import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../../../service/toast.service';
import { ScreenSizeService } from '../../../../../service/screen-size.service';
import { PhotoService } from '../../../../../service/photo.service';
import { DataLoading } from '../../../../../models/DataLoading.interface';
import { AssetsDataService } from '../../../../../service/assets-data.service';
import { LogEntryPhoto } from '../../../../../models/logs/photos/LogEntryPhoto.interface';


@Component({
  selector: 'app-photo-uploader',
  templateUrl: './photo-uploader.component.html',
  styleUrls: ['./photo-uploader.component.css'],
})
export class PhotoUploaderComponent implements OnInit, OnDestroy {
  @Input() photosDisabled: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() dataLoading: DataLoading = { isLoading: false, message: '' };
  /**
   * Whether the mobile "Choose from Library" button is shown. Defaults to
   * true so the shared receiving-log photo flow is unaffected; SOF Receiving
   * sets this to false (camera-only capture — proof photos should be taken
   * on the spot, not picked from an existing library).
   */
  @Input() allowLibrary: boolean = true;
  /**
   * When true, captured photos are emitted through `photosPicked` instead of
   * being pushed into PhotoService's shared logEntryPhotos state. Lets hosts
   * with their own photo state (e.g. SOF Receiving) reuse this component
   * without cross-contaminating the receiving-log flow.
   */
  @Input() emitOnly: boolean = false;
  @Output() photosPicked = new EventEmitter<LogEntryPhoto[]>();


  private dataLoadingSubscription!: Subscription;

  constructor(
    private _toastService: ToastService,
    private _screenSizeService: ScreenSizeService,
    private _photoService: PhotoService,
    private _assetsDataService: AssetsDataService
  ) {
  }

  ngOnInit(): void {
    this.dataLoadingSubscription = this._photoService.dataLoading.subscribe(
      (dataLoading: DataLoading) => {
        this.dataLoading = dataLoading;
      }
    );
  }

  ngOnDestroy(): void {
    if (this.dataLoadingSubscription) {
      this.dataLoadingSubscription.unsubscribe();
    }
  }

  /**
   * Routes a picked/captured file either into the shared PhotoService state
   * (default) or through buildPhotosFromFile + the photosPicked output when
   * emitOnly is set. Both paths share the same validation/compression.
   */
  private async handleFile(file: File): Promise<void> {
    if (!this.emitOnly) {
      await this._photoService.processFile(file);
      return;
    }

    this._photoService.dataLoading.next({
      isLoading: true,
      message: this._photoService.loadingText.processingFile,
    });
    try {
      const newPhotos = await this._photoService.buildPhotosFromFile(file);
      if (newPhotos.length) {
        this.photosPicked.emit(newPhotos);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      this._toastService.showWarningMessage(
        this._photoService.toastText.photos.processing.error
      );
    } finally {
      this._photoService.dataLoading.next({ isLoading: false, message: '' });
    }
  }

  takePicture() {
    if (this.photosDisabled) return;

    console.log('Taking picture...');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';

    const cleanup = () => {
      input.value = '';
      input.remove();
    };

    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        console.log('Photo captured:', file.name);
        this.handleFile(file).then(() => cleanup());
      } else {
        cleanup();
      }
    };

    input.oncancel = cleanup;
    input.click();
  }

  selectFromLibrary() {
    if (this.photosDisabled) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        Array.from(files).forEach(file => {
          this.handleFile(file);
        });
      }
    };

    input.click();
  }

  selectFromDesktop(multiple: boolean = true) {
    if (this.photosDisabled) return;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*,application/pdf';

    if (multiple) {
      fileInput.multiple = true;
    }

    fileInput.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        Array.from(files).forEach(file => {
          this.handleFile(file);
        });
      }
    };

    fileInput.click();
  }

  onDragOver(event: DragEvent) {
    if (this.photosDisabled) return;
    
    event.preventDefault();
    event.stopPropagation();
    const dropArea = event.currentTarget as HTMLElement;
    dropArea.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent) {
    if (this.photosDisabled) return;
    
    event.preventDefault();
    event.stopPropagation();
    const dropArea = event.currentTarget as HTMLElement;
    dropArea.classList.remove('drag-over');
  }

  onDrop(event: DragEvent) {
    if (this.photosDisabled) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const dropArea = event.currentTarget as HTMLElement;
    dropArea.classList.remove('drag-over');
    
    if (event.dataTransfer?.files.length) {
      Array.from(event.dataTransfer.files).forEach(file => {
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
          this.handleFile(file);
        } else {
          this._toastService.showWarningMessage(`${file.name} is not a supported file type`);
        }
      });
    }
  }
} 